/* No analytics, remote services, or customer data storage are used by these enhancements. */
(() => {
  const lockScroll = () => document.documentElement.classList.add('jb-modal-open');
  const releaseScroll = () => {
    if (!document.querySelector('.jb-dialog[open]')) document.documentElement.classList.remove('jb-modal-open');
  };

  class JamabeanHeader extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      const menu = this.querySelector('.jb-mobile-menu');
      if (!menu) return;
      menu.addEventListener('click', (event) => {
        if (event.target.closest('a')) menu.open = false;
      }, { signal });
      document.addEventListener('click', (event) => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu.open) {
          menu.open = false;
          menu.querySelector('summary').focus();
        }
      }, { signal });
    }
    disconnectedCallback() { this.events?.abort(); }
  }

  class JamabeanPause extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-jb-open-pause]');
        if (!trigger || document.querySelector('jamabean-pause') !== this) return;
        this.trigger = trigger;
        if (!this.dialog.open) {
          this.dialog.showModal();
          lockScroll();
        }
      }, { signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-jb-close]') || event.target === this.dialog) this.dialog.close();
        const choice = event.target.closest('[data-jb-energy]');
        if (!choice) return;
        const prompts = {
          low: ['Keep it gentle.', 'Let your shoulders rest. Take a comfortable breath. Ask yourself, “What would make the next ten minutes a little easier?” Choose one small thing.'],
          middle: ['Make a little room.', 'Pause what you can. Notice one thing you can see, one you can hear, and one you can feel. Ask, “What do I need next?” Let that answer be enough.'],
          more: ['Let care ripple outward.', 'Take a comfortable breath. Name one thing that has supported you lately. Choose a small way to care for yourself or reach out to someone you appreciate.']
        };
        const prompt = prompts[choice.dataset.jbEnergy];
        if (!prompt) return;
        this.querySelectorAll('[data-jb-energy]').forEach((button) => button.setAttribute('aria-pressed', String(button === choice)));
        this.querySelector('[data-jb-pause-title]').textContent = prompt[0];
        this.querySelector('[data-jb-pause-copy]').textContent = prompt[1];
      }, { signal });
      this.dialog.addEventListener('close', () => {
        releaseScroll();
        if (this.trigger?.isConnected) this.trigger.focus();
      }, { signal });
    }
    disconnectedCallback() {
      this.events?.abort();
      releaseScroll();
    }
  }

  class JamabeanGallery extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      this.photos = [...this.querySelectorAll('[data-jb-photo]')];
      this.filters = this.querySelector('[data-jb-filters]');
      this.dialog = this.querySelector('dialog');
      this.image = this.querySelector('[data-jb-lightbox-image]');
      if (!this.dialog || !this.image) return;
      this.buildAlbums(signal);
      if (this.filters) {
        this.filters.replaceChildren();
        const names = [...new Set(this.photos.map((photo) => photo.dataset.event))];
        if (this.dataset.filterEnabled === 'true' && names.length > 1) {
          const options = ['', ...names];
          options.forEach((name) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'jb-pill';
            button.dataset.jbFilter = name;
            button.setAttribute('aria-pressed', String(name === ''));
            button.textContent = name || this.dataset.allLabel || 'All events';
            this.filters.append(button);
          });
          this.filters.hidden = false;
        } else this.filters.hidden = true;
      }
      this.addEventListener('click', (event) => {
        const filter = event.target.closest('[data-jb-filter]');
        if (filter) this.filterPhotos(filter.dataset.jbFilter);
        const carouselButton = event.target.closest('[data-jb-carousel-step]');
        if (carouselButton) {
          const album = this.albums.find((entry) => entry.element === carouselButton.closest('[data-jb-album]'));
          if (album) this.goToSlide(album, album.index + Number(carouselButton.dataset.jbCarouselStep));
        }
        const link = event.target.closest('[data-jb-photo-open]');
        if (link) {
          /* Keep normal new-tab and download behavior for modified link clicks. */
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.trigger = link;
          const eventName = link.closest('[data-jb-photo]').dataset.event;
          this.activePhotos = this.photos.filter((photo) => photo.dataset.event === eventName);
          this.index = this.activePhotos.indexOf(link.closest('[data-jb-photo]'));
          if (this.index < 0) return;
          this.showPhoto();
          if (!this.dialog.open) this.dialog.showModal();
          lockScroll();
        }
        if (event.target.closest('[data-jb-close]') || event.target === this.dialog) this.dialog.close();
        if (event.target.closest('[data-jb-prev]')) this.step(-1);
        if (event.target.closest('[data-jb-next]')) this.step(1);
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, { signal });
      this.dialog.addEventListener('close', () => {
        releaseScroll();
        if (this.trigger?.isConnected) this.trigger.focus();
      }, { signal });
      /* Make an editor-selected photo visible even when an event filter was active. */
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        this.filterPhotos('');
        const photo = event.target.closest('[data-jb-photo]');
        const album = this.albums.find((entry) => entry.photos.includes(photo));
        if (album) this.goToSlide(album, album.photos.indexOf(photo), false);
      }, { signal });
      window.addEventListener('resize', () => {
        this.albums.filter((album) => !album.element.hidden).forEach((album) => this.goToSlide(album, album.index, false));
      }, { signal });
      this.filterPhotos('');
    }
    buildAlbums(signal) {
      const grid = this.querySelector('.jb-gallery-grid');
      if (!grid) { this.albums = []; return; }
      /* Restore the original photo elements before rebuilding after an editor reload. */
      this.photos.forEach((photo) => grid.append(photo));
      grid.querySelectorAll('[data-jb-album]').forEach((album) => album.remove());
      const grouped = new Map();
      this.photos.forEach((photo) => {
        const eventName = photo.dataset.event;
        if (!grouped.has(eventName)) grouped.set(eventName, []);
        grouped.get(eventName).push(photo);
      });
      this.albums = [];
      grouped.forEach((photos, name) => {
        const element = document.createElement('article');
        element.className = 'jb-event-album';
        element.dataset.jbAlbum = '';
        element.dataset.event = name;
        element.setAttribute('role', 'region');
        element.setAttribute('aria-roledescription', 'carousel');
        const heading = document.createElement('h3');
        heading.className = 'jb-event-album__title';
        heading.id = `JbAlbum-${this.dataset.sectionId}-${this.albums.length}`;
        heading.textContent = name;
        element.setAttribute('aria-labelledby', heading.id);
        const total = document.createElement('p');
        total.className = 'jb-event-album__total';
        total.textContent = `${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`;
        const track = document.createElement('div');
        track.className = 'jb-carousel-track';
        track.tabIndex = 0;
        track.setAttribute('aria-label', `Photos from ${name}. Use the arrow keys or swipe to browse.`);
        track.id = `${heading.id}-track`;
        const controls = document.createElement('div');
        controls.className = 'jb-carousel-controls';
        controls.hidden = photos.length < 2;
        const previous = document.createElement('button');
        const next = document.createElement('button');
        for (const [button, direction, label, symbol] of [[previous, -1, 'Previous', '←'], [next, 1, 'Next', '→']]) {
          button.type = 'button';
          button.className = 'jb-carousel-arrow';
          button.dataset.jbCarouselStep = direction;
          button.setAttribute('aria-label', `${label} photo from ${name}`);
          button.setAttribute('aria-controls', track.id);
          button.textContent = symbol;
        }
        const status = document.createElement('p');
        status.className = 'jb-carousel-position';
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        controls.append(previous, status, next);
        photos.forEach((photo, index) => {
          photo.setAttribute('role', 'group');
          photo.setAttribute('aria-roledescription', 'slide');
          photo.setAttribute('aria-label', `Photo ${index + 1} of ${photos.length}`);
          track.append(photo);
        });
        element.append(heading, total, track, controls);
        const album = { name, element, photos, track, previous, next, status, index: 0 };
        this.albums.push(album);
        track.addEventListener('scroll', () => {
          const width = track.clientWidth;
          if (width > 0) {
            const index = Math.max(0, Math.min(photos.length - 1, Math.round(track.scrollLeft / width)));
            if (index !== album.index) { album.index = index; this.updateAlbum(album); }
          }
        }, { signal, passive: true });
        track.addEventListener('keydown', (event) => {
          if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const index = event.key === 'Home' ? 0 : event.key === 'End' ? photos.length - 1 : album.index + (event.key === 'ArrowLeft' ? -1 : 1);
          this.goToSlide(album, index);
        }, { signal });
        this.updateAlbum(album);
      });
      grid.prepend(...this.albums.map((album) => album.element));
    }
    updateAlbum(album) {
      album.previous.disabled = album.index === 0;
      album.next.disabled = album.index === album.photos.length - 1;
      album.status.textContent = `${album.index + 1} / ${album.photos.length}`;
    }
    goToSlide(album, index, animate = true) {
      album.index = Math.max(0, Math.min(album.photos.length - 1, index));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      album.track.scrollTo({ left: album.index * album.track.clientWidth, behavior: animate && !reducedMotion ? 'smooth' : 'instant' });
      this.updateAlbum(album);
    }
    filterPhotos(name) {
      let count = 0;
      let events = 0;
      this.albums.forEach((album) => {
        album.element.hidden = Boolean(name && album.name !== name);
        if (!album.element.hidden) {
          count += album.photos.length;
          events += 1;
          this.goToSlide(album, album.index, false);
        }
      });
      this.filters?.querySelectorAll('[data-jb-filter]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.jbFilter === name));
      });
      const status = this.querySelector('[data-jb-gallery-status]');
      if (status) status.textContent = `${count} ${count === 1 ? 'photo' : 'photos'} across ${events} ${events === 1 ? 'event' : 'events'} shown.`;
    }
    showPhoto() {
      const photo = this.activePhotos?.[this.index];
      if (!photo) return;
      this.image.src = photo.querySelector('[data-jb-photo-open]').href;
      this.image.alt = photo.dataset.alt || photo.dataset.event || 'Jamabean community event';
      this.querySelector('[data-jb-photo-event]').textContent = [photo.dataset.event, photo.dataset.date].filter(Boolean).join(' · ');
      this.querySelector('[data-jb-lightbox-caption]').textContent = photo.dataset.caption || '';
      this.querySelector('[data-jb-lightbox-credit]').textContent = photo.dataset.credit || '';
      this.querySelector('[data-jb-photo-counter]').textContent = `${this.index + 1} / ${this.activePhotos.length}`;
      const onlyOne = this.activePhotos.length < 2;
      this.querySelector('[data-jb-prev]').disabled = onlyOne;
      this.querySelector('[data-jb-next]').disabled = onlyOne;
    }
    step(direction) {
      if (!this.activePhotos?.length) return;
      this.index = (this.index + direction + this.activePhotos.length) % this.activePhotos.length;
      this.showPhoto();
    }
    disconnectedCallback() {
      this.events?.abort();
      releaseScroll();
    }
  }

  if (!customElements.get('jamabean-header')) customElements.define('jamabean-header', JamabeanHeader);
  if (!customElements.get('jamabean-pause')) customElements.define('jamabean-pause', JamabeanPause);
  if (!customElements.get('jamabean-gallery')) customElements.define('jamabean-gallery', JamabeanGallery);
})();
